import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import React, { useEffect, useRef } from "react";
import { css } from "styled-components";

const enum fields {
    scriptContent = "scriptContent",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.scriptContent]: string;
    };
}

type Props = OwnProps & HasShellContext;

const FooterScriptInjector = ({ fields, shellContext, id }: Props) => {
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (wrapperRef.current && fields.scriptContent) {
            const fragment = document.createRange().createContextualFragment(fields.scriptContent);
            wrapperRef.current.innerHTML = "";
            wrapperRef.current.appendChild(fragment);
        }
    }, []);

    // In the CMS editor shell, show a placeholder instead of executing scripts
    if (shellContext.isInShell && shellContext.isEditing) {
        return (
            <StyledWrapper
                css={css`
                    background: #f0f0f0;
                    padding: 10px;
                    border: 1px dashed #ccc;
                `}
            >
                <Typography variant="h6" css={css` margin: 0; `}>
                    {translate("Footer Script Injector")}
                </Typography>
                <Typography>
                    Scripts will execute on the live storefront, not in the CMS editor.
                </Typography>
            </StyledWrapper>
        );
    }

    // Server-side rendering: use dangerouslySetInnerHTML
    if (IS_SERVER_SIDE) {
        return <div dangerouslySetInnerHTML={{ __html: fields.scriptContent }} />;
    }

    // Client-side: use ref + createContextualFragment so <script> tags execute
    return <div id={`footer-script_${id}`} ref={wrapperRef} />;
};

const widgetModule: WidgetModule = {
    component: withIsInShell(FooterScriptInjector),
    definition: {
        group: "Basic",
        icon: "code",
        allowedContexts: ["Footer"],
        fieldDefinitions: [
            {
                name: fields.scriptContent,
                displayName: "Script Content",
                editorTemplate: "CodeSnippetField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Paste your JavaScript snippet here, including <script> tags.",
            },
        ],
    },
};

export default widgetModule;